<template>
  <section class="safari-summary__card honeymoon-tasks">
    <div class="safari-summary__header">
      <div>
        <h2 class="safari-summary__title">Honeymoon tasks</h2>
        <p class="honeymoon-tasks__subtitle">Prepare the personalised welcome PDF</p>
      </div>
      <span class="honeymoon-tasks__progress">{{ completedCount }}/5</span>
    </div>

    <div v-if="loading" class="honeymoon-tasks__empty">Loading checklist…</div>
    <div v-else-if="!document" class="honeymoon-tasks__empty">Checklist is unavailable</div>
    <ol v-else class="honeymoon-tasks__list">
      <li :class="['honeymoon-task', { 'honeymoon-task--done': document.generated }]">
        <span class="honeymoon-task__state">{{ document.generated ? '✓' : '1' }}</span>
        <div class="honeymoon-task__content">
          <div class="honeymoon-task__heading">
            <strong>Generate wheel cover</strong>
            <div class="honeymoon-task__actions">
              <a
                v-if="document.file"
                :href="document.file.file"
                target="_blank"
                rel="noopener"
                class="honeymoon-task__pdf-button"
              >
                <img src="@/assets/img/downloads-pdf.svg" alt="">
                Open PDF
              </a>
              <app-button
                v-if="canManage && !document.sent_for_printing"
                size="small"
                theme="info"
                :disabled="busy || !canGenerate"
                @click.native="onGenerate"
              >
                {{ busyAction === 'generate' ? 'Working…' : document.generated ? 'Regenerate' : 'Generate' }}
              </app-button>
            </div>
          </div>
          <div class="honeymoon-task__names">
            <label class="honeymoon-task__name-field">
              <span>Bride’s name</span>
              <app-input
                v-model="firstName"
                placeholder="Enter bride’s name"
                :readonly="!canManage || document.sent_for_printing"
              />
            </label>
            <label class="honeymoon-task__name-field">
              <span>Groom’s name</span>
              <app-input
                v-model="secondName"
                placeholder="Enter groom’s name"
                :readonly="!canManage || document.sent_for_printing"
              />
            </label>
          </div>
          <task-actor
            v-if="document.generated_by"
            :actor="document.generated_by"
            :date="document.generated_at"
            action="Generated"
          />
        </div>
      </li>

      <li :class="['honeymoon-task', { 'honeymoon-task--done': document.sent_for_printing }]">
        <span class="honeymoon-task__state">{{ document.sent_for_printing ? '✓' : '2' }}</span>
        <div class="honeymoon-task__content">
          <div class="honeymoon-task__heading">
            <strong>Sent for printing</strong>
            <app-switcher
              :value="document.sent_for_printing"
              :readonly="!canManage || !document.generated || document.printed || document.complimentary_wine_added || document.completed || busy"
              @change="$emit('status-change', 'sent-for-printing', $event)"
            />
          </div>
          <task-actor
            v-if="document.sent_for_printing_by"
            :actor="document.sent_for_printing_by"
            :date="document.sent_for_printing_at"
            action="Sent for printing"
          />
        </div>
      </li>

      <li :class="['honeymoon-task', { 'honeymoon-task--done': document.printed }]">
        <span class="honeymoon-task__state">{{ document.printed ? '✓' : '3' }}</span>
        <div class="honeymoon-task__content">
          <div class="honeymoon-task__heading">
            <strong>Printed and received at the office</strong>
            <app-switcher
              :value="document.printed"
              :readonly="!canManage || !document.sent_for_printing || document.complimentary_wine_added || document.completed || busy"
              @change="$emit('status-change', 'printed', $event)"
            />
          </div>
          <task-actor
            v-if="document.printed_by"
            :actor="document.printed_by"
            :date="document.printed_at"
            action="Received at the office"
          />
        </div>
      </li>

      <li :class="['honeymoon-task', { 'honeymoon-task--done': document.complimentary_wine_added }]">
        <span class="honeymoon-task__state">{{ document.complimentary_wine_added ? '✓' : '4' }}</span>
        <div class="honeymoon-task__content">
          <div class="honeymoon-task__heading">
            <strong>Complimentary wine added</strong>
            <app-switcher
              :value="document.complimentary_wine_added"
              :readonly="!canManage || !document.printed || document.completed || busy"
              @change="$emit('status-change', 'complimentary-wine-added', $event)"
            />
          </div>
          <task-actor
            v-if="document.complimentary_wine_added_by"
            :actor="document.complimentary_wine_added_by"
            :date="document.complimentary_wine_added_at"
            action="Wine added"
          />
        </div>
      </li>

      <li :class="['honeymoon-task', { 'honeymoon-task--done': document.completed }]">
        <span class="honeymoon-task__state">{{ document.completed ? '✓' : '5' }}</span>
        <div class="honeymoon-task__content">
          <div class="honeymoon-task__heading">
            <strong>Passed to the driver</strong>
            <app-switcher
              :value="document.completed"
              :readonly="!canManage || !document.complimentary_wine_added || busy"
              @change="$emit('status-change', 'completed', $event)"
            />
          </div>
          <task-actor
            v-if="document.completed_by"
            :actor="document.completed_by"
            :date="document.completed_at"
            action="Passed to the driver"
          />
        </div>
      </li>
    </ol>
  </section>
</template>

<script>
import TaskActor from './HoneymoonTaskActor.vue'

export default {
  name: 'HoneymoonTasks',
  components: { TaskActor },
  props: {
    document: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    busyAction: { type: String, default: null },
    canManage: { type: Boolean, default: false },
  },
  data() {
    return {
      firstName: '',
      secondName: '',
    }
  },
  computed: {
    busy() {
      return Boolean(this.busyAction)
    },
    canGenerate() {
      return Boolean(this.firstName?.trim() && this.secondName?.trim())
    },
    completedCount() {
      if (!this.document) return 0
      return [
        'generated',
        'sent_for_printing',
        'printed',
        'complimentary_wine_added',
        'completed',
      ]
        .filter(field => this.document[field]).length
    },
  },
  watch: {
    document: {
      immediate: true,
      handler(document) {
        if (!document) return
        this.firstName = document.first_name || this.firstName
        this.secondName = document.second_name || this.secondName
      },
    },
  },
  methods: {
    onGenerate() {
      if (!this.canGenerate || this.busy) return
      this.$emit('generate', {
        first_name: this.firstName.trim(),
        second_name: this.secondName.trim(),
      })
    },
  },
}
</script>

<style scoped>
.honeymoon-tasks {
  box-sizing: border-box;
  min-width: 0;
  padding: 18px 20px;
  border: 1px solid #e2e2e2;
  border-radius: 10px;
  background: #fff;
}
.safari-summary__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.safari-summary__title { margin: 0; color: #202020; font-size: 16px; line-height: 25px; }
.honeymoon-tasks__subtitle { margin: 2px 0 0; color: #8a8a8a; font-size: 12px; }
.honeymoon-tasks__progress {
  padding: 4px 8px;
  border-radius: 12px;
  background: #f2f6ec;
  color: #679c0d;
  font-size: 12px;
  font-weight: 600;
}
.honeymoon-tasks__empty { padding: 20px 0; color: #8a8a8a; font-size: 13px; }
.honeymoon-tasks__list { margin: 0; padding: 0; list-style: none; }
.honeymoon-task { display: flex; gap: 11px; padding: 12px 0; border-top: 1px solid #ececec; }
.honeymoon-task:first-child { padding-top: 0; border-top: 0; }
.honeymoon-task:last-child { padding-bottom: 0; }
.honeymoon-task__state {
  display: flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid #d5d5d5;
  border-radius: 50%;
  color: #8a8a8a;
  font-size: 12px;
  font-weight: 600;
}
.honeymoon-task--done .honeymoon-task__state { border-color: #77b017; background: #77b017; color: #fff; }
.honeymoon-task__content { min-width: 0; flex: 1; }
.honeymoon-task__heading { display: flex; min-height: 24px; align-items: center; justify-content: space-between; gap: 10px; }
.honeymoon-task__heading strong { color: #202020; font-size: 14px; }
.honeymoon-task__actions { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 8px; }
.honeymoon-task__pdf-button {
  display: inline-flex;
  min-height: 32px;
  box-sizing: border-box;
  align-items: center;
  gap: 7px;
  padding: 5px 11px;
  border: 1px solid #35a4ea;
  border-radius: 8px;
  background: #f3faff;
  color: #218ed3;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  transition: background-color .2s, box-shadow .2s;
}
.honeymoon-task__pdf-button img { width: 15px; height: 20px; }
.honeymoon-task__pdf-button:hover,
.honeymoon-task__pdf-button:focus { background: #e5f4fd; box-shadow: 0 2px 7px rgba(53, 164, 234, .18); }
.honeymoon-task__names { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 9px; }
.honeymoon-task__name-field { display: grid; gap: 5px; color: #606060; font-size: 11px; font-weight: 600; }
@media (max-width: 420px) {
  .honeymoon-task__names { grid-template-columns: 1fr; }
}
</style>
